import React from "react";

import {
  OrderByDirection,
  WhereFilterOp,
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  setDoc,
  startAfter,
  updateDoc,
  where,
} from "firebase/firestore";
import { deleteObject, getStorage, ref } from "firebase/storage";

import app from "../firebase";

import { Dev } from "../util/consoleUtil";
import firebaseError from "../util/firebaseError";

export type FirebaseFilter = { field: string; opStr: WhereFilterOp; value: unknown };
export type FirebaseOrder = { field: string; direction?: OrderByDirection };

const db = getFirestore(app);
const storage = getStorage(app);

/** One */

type OneResult<T extends unknown> = {
  item: T | null;
  isLoading: boolean;
  runRequest: (itemId: string) => Promise<T | null>;
};

export const RequestOne = <T extends unknown>(collectionId: string): OneResult<T> => {
  const [isLoading, setLoading] = React.useState(false);

  const [item, setItem] = React.useState<T | null>(null);

  const runRequest = async (itemId: string) => {
    return new Promise<T | null>(async (Ok, Rej) => {
      try {
        setLoading(true);
        const mainRef = doc(db, collectionId, itemId);
        const mainSnap = await getDoc(mainRef);
        setLoading(false);

        const itemObj = { id: itemId, ...mainSnap.data() } as T;

        if (!mainSnap.exists()) return Rej(new Error("Item não existe."));

        setItem(itemObj);

        Ok(itemObj);
      } catch (error) {
        console.log(...new Dev("Request Error [one]", error).Error());
        setLoading(false);
        Rej(new Error("Erro ao obter dados. " + firebaseError(error)));
      }
    });
  };

  return { item, isLoading, runRequest };
};

/** List */

type ListResult<T extends unknown> = {
  items: T[];
  isLoading: boolean;
  runRequest: (
    filter?: FirebaseFilter,
    order?: FirebaseOrder,
    definedLimit?: number
  ) => Promise<T[]>;
};

export const RequestList = <T extends unknown>(collectionId: string): ListResult<T> => {
  const [isLoading, setLoading] = React.useState(false);

  const [items, setItems] = React.useState<T[]>([]);

  const runRequest = async (
    filter?: FirebaseFilter,
    order?: FirebaseOrder,
    definedLimit: number = 0
  ) => {
    return new Promise<T[]>(async (Ok, Rej) => {
      try {
        setLoading(true);
        const mainRef = collection(db, collectionId);
        const args = [] as any[];
        if (filter) args.push(where(filter.field, filter.opStr, filter.value));
        if (order) args.push(orderBy(order?.field, order?.direction));
        if (definedLimit) args.push(limit(definedLimit));
        const queryRef = query(mainRef, ...args);
        const mainSnap = await getDocs(queryRef);
        setLoading(false);

        const listData = [] as T[];

        mainSnap.forEach((doc) => {
          listData.push({ id: doc.id, ...doc.data() } as T);
        });

        setItems(listData);

        Ok(listData);
      } catch (error) {
        console.log(...new Dev("Request Error [list]", error).Error());
        setLoading(false);
        Rej(new Error("Erro ao obter dados. " + firebaseError(error)));
      }
    });
  };

  return { items, isLoading, runRequest };
};

/** Lazy List */

type LazyListResult<T extends unknown> = {
  items: T[];
  isLoading: boolean;
  hasMore: boolean;
  runRequest: (
    restart?: boolean,
    filter?: { field: string; opStr: WhereFilterOp; value: unknown }
  ) => Promise<T[]>;
};

export const RequestLazyList = <T extends unknown>(
  collectionId: string,
  definedLimit: number = 5,
  orderField: string = "createdAtTimestamp",
  orderDirection: "asc" | "desc" = "asc",
  itemsLimit: number | false = false,
  loadsLimit: number | false = false
): LazyListResult<T> => {
  const [isLoading, setLoading] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(false);

  const [lastItemRef, setLastItemRef] = React.useState<any>(null);

  const [items, setItems] = React.useState<T[]>([]);

  const runRequest = async (
    restart?: boolean,
    filter?: { field: string; opStr: WhereFilterOp; value: unknown }
  ) => {
    return new Promise<T[]>(async (Ok, Rej) => {
      try {
        setLoading(true);

        const mainRef = collection(db, collectionId);

        const args = [orderBy(orderField, orderDirection)] as any[];

        if (lastItemRef && !restart) {
          args.push(startAfter(lastItemRef));
        }

        args.push(limit(definedLimit));

        if (filter) {
          args.unshift(where(filter.field, filter.opStr, filter.value));
        }

        const queryRef = query(mainRef, ...args);

        const mainSnap = await getDocs(queryRef);

        setLastItemRef(mainSnap.docs[mainSnap.docs.length - 1] || null);

        const listData = [] as T[];

        mainSnap.forEach((doc) => {
          listData.push({ id: doc.id, ...doc.data() } as T);
        });

        setItems(restart ? [...listData] : [...items, ...listData]);

        if (listData.length < definedLimit) setHasMore(false);
        else setHasMore(true);

        setLoading(false);

        Ok(listData);
      } catch (error) {
        console.log(...new Dev("Request Error [lazyList]", error).Error());
        setLoading(false);
        Rej(new Error("Erro ao obter dados. " + firebaseError(error)));
      }
    });
  };

  return { items, isLoading, runRequest, hasMore };
};

/** Create */

type CreateResult<T extends unknown> = {
  isLoading: boolean;
  runRequest: (itemData: T) => Promise<boolean>;
};

export const RequestCreate = <T extends unknown>(collectionId: string): CreateResult<T> => {
  const [isLoading, setLoading] = React.useState(false);

  const runRequest = async (itemData: T) => {
    return new Promise<boolean>(async (Ok, Rej) => {
      try {
        setLoading(true);

        const mainRef = collection(db, collectionId);
        const mainSnap = await addDoc(mainRef, itemData as any);

        setLoading(false);
        Ok(true);
      } catch (error) {
        console.log(...new Dev("Request Error [create]", error).Error());
        setLoading(false);
        Rej(new Error("Erro ao criar dados. " + firebaseError(error)));
      }
    });
  };

  return { isLoading, runRequest };
};

/** Update */

type UpdateResult<T extends unknown> = {
  isLoading: boolean;
  runRequest: (itemId: string, itemData: T) => Promise<boolean>;
};

export const RequestUpdate = <T extends unknown>(collectionId: string): UpdateResult<T> => {
  const [isLoading, setLoading] = React.useState(false);

  const runRequest = async (itemId: string, itemData: T, isMerge?: boolean) => {
    return new Promise<boolean>(async (Ok, Rej) => {
      try {
        setLoading(true);
        const mainRef = doc(db, collectionId, itemId);
        if (isMerge) await setDoc(mainRef, itemData as any, { merge: true });
        else await updateDoc(mainRef, itemData as any);
        setLoading(false);

        Ok(true);
      } catch (error) {
        console.log(...new Dev("Request Error [update]", error).Error());
        setLoading(false);
        Rej(new Error("Erro ao atualizar dados. " + firebaseError(error)));
      }
    });
  };

  return { isLoading, runRequest };
};

/** Update Array */

type UpdateArrayResult = {
  isLoading: boolean;
  runRequest: (itemId: string, fieldKey: string, itemData: any) => Promise<boolean>;
};

export const RequestUpdateArray = (
  collectionId: string,
  action: "add" | "remove"
): UpdateArrayResult => {
  const [isLoading, setLoading] = React.useState(false);

  const runRequest = async (
    itemId: string,
    fieldKey: string,
    itemData: number | string | boolean
  ) => {
    return new Promise<boolean>(async (Ok, Rej) => {
      try {
        setLoading(true);
        const mainRef = doc(db, collectionId, itemId);
        const updateAction = action === "add" ? arrayUnion(itemData) : arrayRemove(itemData);
        const mainSnap = await updateDoc(mainRef, { [fieldKey]: updateAction });
        setLoading(false);

        Ok(true);
      } catch (error) {
        console.log(...new Dev("Request Error [updateArray]", error).Error());
        setLoading(false);
        Rej(new Error("Erro ao atualizar dados. " + firebaseError(error)));
      }
    });
  };

  return { isLoading, runRequest };
};

/** Remove */

type RemoveResult = {
  isLoading: boolean;
  runRequest: (itemId: string) => Promise<boolean>;
};

export const RequestRemove = (collectionId: string): RemoveResult => {
  const [isLoading, setLoading] = React.useState(false);

  const runRequest = async (itemId: string) => {
    return new Promise<boolean>(async (Ok, Rej) => {
      try {
        setLoading(true);
        const mainRef = doc(db, collectionId, itemId);
        const mainSnap = await deleteDoc(mainRef);
        setLoading(false);

        Ok(true);
      } catch (error) {
        console.log(...new Dev("Request Error [remove]", error).Error());
        setLoading(false);
        Rej(new Error("Erro ao remover dados. " + firebaseError(error)));
      }
    });
  };

  return { isLoading, runRequest };
};

/** Remove File */

type RemoveFileResult = {
  isLoading: boolean;
  runRequest: (reference: string) => Promise<boolean>;
};

export const RequestRemoveFile = (): RemoveFileResult => {
  const [isLoading, setLoading] = React.useState(false);

  const runRequest = async (reference: string) => {
    return new Promise<boolean>(async (Ok, Rej) => {
      try {
        setLoading(true);
        const fileRef = ref(storage, reference);
        const delSnap = await deleteObject(fileRef);
        setLoading(false);

        Ok(true);
      } catch (error) {
        console.log(...new Dev("Request Error [removeFile]", error).Error());
        setLoading(false);
        Rej(new Error("Erro ao remover arquivo. " + firebaseError(error)));
      }
    });
  };

  return { isLoading, runRequest };
};

/** Count */

type CountResult = {
  count: number | null;
  isLoading: boolean;
  runRequest: () => Promise<number>;
};

export const RequestCount = (collectionId: string): CountResult => {
  const [isLoading, setLoading] = React.useState(false);

  const [count, setCount] = React.useState<number | null>(null);

  const runRequest = async () => {
    return new Promise<number>(async (Ok, Rej) => {
      try {
        setLoading(true);
        const mainRef = collection(db, collectionId);
        const mainSnap = await getCountFromServer(mainRef);
        setLoading(false);

        const countValue = mainSnap.data().count;
        setCount(countValue);

        Ok(countValue);
      } catch (error) {
        console.log(...new Dev("Request Error [count]", error).Error());
        setLoading(false);
        Rej(new Error("Erro ao obter dados. " + firebaseError(error)));
      }
    });
  };

  return { count, isLoading, runRequest };
};
